import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { Feature, Map } from 'ol'
import {
    IMapParcelToGroupDrawing,
    IMapStateMeasureDrawing,
} from '../../../../../features/map/interfaces/map-state.interface'
import { Vector as VectorLayer } from 'ol/layer'
import VectorSource from 'ol/source/Vector'
import { Geometry } from 'ol/geom'

@Injectable({
    providedIn: 'root',
})
export class GeneralMapService {

    public map$ = new BehaviorSubject<Record<string, Map>>({})
    public measurementClosedAt = 0
    public measureDrawing$ = new BehaviorSubject<Record<string, IMapStateMeasureDrawing>>({})
    public featureGroupLayers$ = new BehaviorSubject<Record<string, Record<string, VectorLayer<any>>>>({})
    public parcelToGroupDrawing$ = new BehaviorSubject<Record<string, IMapParcelToGroupDrawing>>({})
    public detailFeature$ = new BehaviorSubject<Record<string, {detailFeature: Feature; backlink: boolean}>>({})

    private INIT_PARCEL_TO_GROUP_DRAWING: IMapParcelToGroupDrawing = {
        drawInfoboxFeature: null,
        drawInfoboxEditedFeature: null,
        drawingVectorSource: null,
        drawnAreas: null,
        intersectParcels: [],
        // SPLIT
        splitBoxOriginalFeature: null,
        splitBoxFirstFeature: null,
        splitBoxSecondFeature: null,
        splitBoxLineFeature: null
    }

    public initMap(mapId: string): void {
        if (!this.map$.value[mapId]) {
            // Never cloneDeep the map objects
            const updateMaps = this.map$.value
            updateMaps[mapId] = undefined
            this.map$.next(updateMaps)
        }
    }

    public setMap(mapId: string, map: Map): void {
        // Never cloneDeep the map objects
        const updateMaps = this.map$.value
        updateMaps[mapId] = map
        this.map$.next(updateMaps)
    }

    public getMap(mapId: string): Map {
        return this.map$.value[mapId]
    }

    public resetMap(mapId: string): void {
        const updateMaps = this.map$.value
        delete updateMaps[mapId]
        this.map$.next(updateMaps)
    }

    // ==> measureDrawing

    public setMeasureDrawing(mapId: string, measureDrawing: IMapStateMeasureDrawing): void {
        const updatedMeasureDrawings = this.measureDrawing$.value
        updatedMeasureDrawings[mapId] = measureDrawing
        this.measureDrawing$.next(updatedMeasureDrawings)
    }

    public getMaMeasureDrawing(mapId: string): IMapStateMeasureDrawing {
        return this.measureDrawing$.value[mapId]
    }

    public resetMeasureDrawing(mapId: string): void {
        delete this.measureDrawing$.value[mapId]
    }

    // ==> featureGroupLayers

    public setFeatureGroupLayersLayer(mapId: string, layerId: string, layer: VectorLayer<any>): void {
        const updatedFeatureGroupLayers = this.featureGroupLayers$.value
        if (!updatedFeatureGroupLayers[mapId]) {
            updatedFeatureGroupLayers[mapId] = {}
        }
        updatedFeatureGroupLayers[mapId][layerId] = layer
        this.featureGroupLayers$.next(updatedFeatureGroupLayers)
    }

    public hasFeatureGroupLayersLayer(mapId: string, layerName: string): boolean {
        return this.featureGroupLayers$.value[mapId]?.[layerName] !== undefined && this.featureGroupLayers$.value[mapId]?.[layerName] !== null
    }

    public getFeatureGroupLayersLayer(mapId: string, layerName: string): VectorLayer<any> {
        // `?.` na [mapId] — bez něj to hodí TypeError, když mapa v registru ještě není,
        // což už není tichá chyba, ale rozbitá mapa (DEV-2101)
        return this.featureGroupLayers$.value[mapId]?.[layerName]
    }

    public resetFeatureGroupLayersLayer(mapId: string, layerName: string): void {
        const updatedFeatureGroupLayers = this.featureGroupLayers$.value
        delete updatedFeatureGroupLayers[mapId]?.[layerName]
        this.featureGroupLayers$.next(updatedFeatureGroupLayers)
    }

    public resetFeatureGroupLayers(mapId: string): void {
        const updatedFeatureGroupLayers = this.featureGroupLayers$.value
        updatedFeatureGroupLayers[mapId]= {}
        this.featureGroupLayers$.next(updatedFeatureGroupLayers)
    }

    // ==> parcelToGroupDrawing

    // drawingVectorSource
    public setParcelToGroupDrawingDrawingVectorSource(mapId: string, drawingVectorSource: VectorSource): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].drawingVectorSource = drawingVectorSource
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    public removeDrawingVectorSourceFeature(mapId: string, feature: Feature): void {
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].drawingVectorSource.removeFeature(feature)
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    public resetParcelToGroupDrawingDrawingVectorSource(mapId: string): void {
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].drawingVectorSource = null
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    public getParcelToGroupDrawingDrawingVectorSource(mapId: string): VectorSource {
        return this.parcelToGroupDrawing$.value[mapId]?.drawingVectorSource
    }
    // drawInfoboxFeature
    public setParcelToGroupDrawingDrawInfoboxFeature(mapId: string, drawInfoboxFeature: Feature<Geometry>): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].drawInfoboxFeature = drawInfoboxFeature
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    public resetParcelToGroupDrawingDrawInfoboxFeature(mapId: string): void {
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].drawInfoboxFeature = null
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    // drawInfoboxEditedFeature
    public setParcelToGroupDrawingDrawInfoboxEditedFeature(mapId: string, drawInfoboxFeature: Feature<Geometry>): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].drawInfoboxEditedFeature = drawInfoboxFeature
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    public getParcelToGroupDrawingDrawInfoboxEditedFeature(mapId: string): Feature {
        return this.parcelToGroupDrawing$.value[mapId]?.drawInfoboxEditedFeature
    }
    // drawnAreas
    public async setParcelToGroupDrawingDrawnAreas(mapId: string, areas: Feature[]): Promise<void> {
        await this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].drawnAreas = areas
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    // intersectParcels
    public setParcelToGroupDrawingIntersectParcels(mapId: string, features: Feature[], featuresToRemove: Feature[]): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        if (featuresToRemove?.length) {
            featuresToRemove.map(item => {
                updatedParcelToGroupDrawing[mapId].intersectParcels = [...updatedParcelToGroupDrawing[mapId].intersectParcels.filter(el => el.get('par_id') !== item.get('par_id'))]
            })
        }
        updatedParcelToGroupDrawing[mapId].intersectParcels = [...features, ...updatedParcelToGroupDrawing[mapId].intersectParcels]
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    public getParcelToGroupDrawingIntersectParcels(mapId: string): Feature[] {
        return this.parcelToGroupDrawing$.value[mapId]?.intersectParcels
    }
    // detailFeature
    public setDetailFeature(mapId: string, feature: Feature<Geometry>, backlink = false): void {
        const updatedDetailFeature = this.detailFeature$.value
        if (!updatedDetailFeature[mapId]) {
            updatedDetailFeature[mapId] = { detailFeature: null, backlink: false }
        }
        updatedDetailFeature[mapId].detailFeature = feature
        updatedDetailFeature[mapId].backlink = backlink
        this.detailFeature$.next(updatedDetailFeature)
    }
    public getDetailFeature(mapId: string): Feature {
        return this.detailFeature$.value[mapId].detailFeature
    }
    // splitBoxOriginalFeature
    public setParcelToGroupDrawingSplitBoxOriginalFeature(mapId: string, feature: Feature<Geometry>): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].splitBoxOriginalFeature = feature
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    public getParcelToGroupDrawingSplitBoxOriginalFeature(mapId: string): Feature {
        return this.parcelToGroupDrawing$.value[mapId]?.splitBoxOriginalFeature
    }
    // splitBoxFirstFeature
    public setParcelToGroupDrawingSplitBoxFirstFeature(mapId: string, feature: Feature[]): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].splitBoxFirstFeature = feature
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    // splitBoxSecondFeature
    public setParcelToGroupDrawingSplitBoxSecondFeature(mapId: string, feature: Feature[]): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].splitBoxSecondFeature = feature
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }
    // splitBoxSecondFeature
    public setParcelToGroupDrawingSplitBoxLineFeature(mapId: string, feature: Feature): void {
        this.initParcelToGroupDrawing(mapId)
        const updatedParcelToGroupDrawing = this.parcelToGroupDrawing$.value
        updatedParcelToGroupDrawing[mapId].splitBoxLineFeature = feature
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }

    public getParcelToGroupDrawingSplitBoxLineFeature(mapId: string): Feature {
        return this.parcelToGroupDrawing$.value[mapId]?.splitBoxLineFeature
    }


    public resetMapDrawing(mapId: string, fullReset: boolean): void {
        if (!this.parcelToGroupDrawing$.value[mapId]) {
            return
        }
        const updatedParcelToGroupDrawing = (this.parcelToGroupDrawing$.value)
        updatedParcelToGroupDrawing[mapId].drawInfoboxFeature = null
        updatedParcelToGroupDrawing[mapId].drawInfoboxEditedFeature = null
        updatedParcelToGroupDrawing[mapId].drawingVectorSource = null
        if (fullReset) {
            updatedParcelToGroupDrawing[mapId].drawnAreas = null
            updatedParcelToGroupDrawing[mapId].intersectParcels = []
        }
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)
    }

    public resetMapSplitting(mapId: string): void {
        if (!this.parcelToGroupDrawing$.value[mapId]) {
            return
        }
        const updatedParcelToGroupDrawing = (this.parcelToGroupDrawing$.value)
        updatedParcelToGroupDrawing[mapId].splitBoxOriginalFeature = null
        updatedParcelToGroupDrawing[mapId].splitBoxFirstFeature = null
        updatedParcelToGroupDrawing[mapId].splitBoxSecondFeature = null
        updatedParcelToGroupDrawing[mapId].splitBoxLineFeature = null
        this.parcelToGroupDrawing$.next(updatedParcelToGroupDrawing)

    }

    // ==> PRIVATE
    private initParcelToGroupDrawing(mapId: string): Promise<void> {
        return new Promise(resolve => {
            if (!this.parcelToGroupDrawing$.value[mapId]) {
                const newParcelToGroupDrawing = this.parcelToGroupDrawing$.value
                newParcelToGroupDrawing[mapId] = this.INIT_PARCEL_TO_GROUP_DRAWING
                this.parcelToGroupDrawing$.next(newParcelToGroupDrawing)
                resolve()
            }
            resolve()
        })
    }
}